import { defineStore } from 'pinia'
import { useUserStore } from '../stores/user'
import { useFirebase } from '../services/firebase'
import { closeSession } from '../services/auth'
import {
  //Auth utils
  getAuth,
  onAuthStateChanged,
  updateProfile,
  signOut,
  //for google login/register)
  GoogleAuthProvider,
  signInWithPopup,
  //for email & pass login/register
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  updatePassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  //for appleId login/register
} from 'firebase/auth'

export const useFirebaseStore = defineStore('firebase', () => {
  const { auth } = useFirebase()
  const userX = useUserStore()
  auth.languageCode = 'es'
  function sendResponse(status: boolean, data: any) {
    return { success: status, data: data }
  }

  function listenAuthChanges() {
    onAuthStateChanged(
      auth,
      async (user) => {
        if (user) {
          userX.setFirebaseUser(user)
        } else {
          //Have to Logout
          userX.setFirebaseUser(null)
        }
      },
      () => {
        //Have to Logout
        userX.setFirebaseUser(null)
      },
    )
  }


  async function login(email: string, password: string, keepLoggedIn: boolean) {
    try {
      const persistenceType = keepLoggedIn ? browserLocalPersistence : browserSessionPersistence
      await setPersistence(auth, persistenceType)
      await signInWithEmailAndPassword(auth, email, password)
      const user = auth.currentUser
      return sendResponse(true, { user })
    } catch (error: any) {
      console.error(error)
      switch (error.code) {
        case 'auth/user-not-found':
          return sendResponse(false, { msg: 'error.userNotFound' })
        case 'auth/wrong-password':
          return sendResponse(false, { msg: 'error.wrongPassword' })
        case 'auth/invalid-email':
          return sendResponse(false, { msg: 'error.invalidEmail' })

        default:
          return sendResponse(false, { msg: 'error.something' })
      }
    }
  }

  async function googleLogin(keepLoggedIn: boolean) {
    try {
      const persistenceType = keepLoggedIn ? browserLocalPersistence : browserSessionPersistence
      await setPersistence(auth, persistenceType)
      const provider = new GoogleAuthProvider()
      await signInWithPopup(auth, provider)
      const user = auth.currentUser
      return sendResponse(true, { user, msg: 'auth.loginSuccess' })
    } catch (error) {
      console.log(error)
      return sendResponse(false, { msg: 'error.something' })
    }
  }

  async function register(email: string, password: string, name: string) {
    try {
      await createUserWithEmailAndPassword(auth, email, password)
      const user = auth.currentUser
      await updateProfile(user!, {
        displayName: name,
      })

      sendConfirmationCode()
      await logout()
      return {
        success: true,
        data: {
          msg: '',
        },
      }
    } catch (error: any) {
      console.log(error)
      switch (error.code) {
        case 'auth/email-already-in-use':
          return sendResponse(false, { msg: 'error.emailUsed' })
        case 'auth/invalid-email':
          return sendResponse(false, { msg: 'error.invalidEmail' })
        case 'auth/weak-password':
          return sendResponse(false, { msg: 'error.weakPassword' })

        default:
          return sendResponse(false, { msg: 'error.something' })
      }
    }
  }

  async function logout() {
    try {
      //close api session
      await closeSession()
      //remove data from localstorage
      userX.setApiUser(null)
      //close firebase session
      await signOut(auth)
      return sendResponse(true, { msg: 'auth.logoutSuccess' })
    } catch (error) {
      console.log(error)
      return sendResponse(false, { msg: 'error.something' })
    }
  }

  async function sendConfirmationCode() {
    try {
      const user = auth.currentUser
      await sendEmailVerification(user!)
      return sendResponse(true, { msg: 'auth.verificationSended' })
    } catch (error) {
      console.log(error)
      return sendResponse(false, { msg: 'error.something' })
    }
  }

  async function recover(email: string) {
    try {
      await sendPasswordResetEmail(auth, email)
      return sendResponse(true, { msg: 'auth.recoverSended' })
    } catch (error: any) {
      switch (error.code) {
        case 'auth/user-not-found':
          return sendResponse(false, { msg: 'error.userNotFound' })
        case 'auth/invalid-email':
          return sendResponse(false, { msg: 'error.invalidEmail' })

        default:
          return sendResponse(false, { msg: 'error.something' })
      }
    }
  }

  async function passwordUpdate(password: string) {
    try {
      const user = auth.currentUser
      await updatePassword(user!, password)
      return sendResponse(true, { msg: 'auth.changingPasswordSuccess' })
    } catch (error) {
      console.log(error)
      return sendResponse(false, { msg: 'error.something' })
    }
  }

  return {
    login,
    googleLogin,
    register,
    logout,
    sendConfirmationCode,
    listenAuthChanges,
    recover,
    passwordUpdate,
    /*


    requestPermission,*/
  }
})
